<script setup lang="ts">
/**
 * Rahmen der Anwendung: Kopfleiste plus Seitennavigation nach Spec §12.
 *
 * Die Navigation ist bewusst flach — Dashboard, Produkte, Priorisierung,
 * Einstellungen. Die Unterseiten eines Produkts (Übersicht/Features/Roadmap)
 * haengen nicht als aufklappbarer Baum in der Seitenleiste, sondern als Reiter IM
 * Produkt: eine Seitenleiste, die mit jedem neuen Produkt weiterwächst, wird
 * schnell unbrauchbar.
 *
 * (Der in §12 ebenfalls genannte Jira-Reiter fehlt, solange die Anbindung aus §11
 * nicht existiert — er käme sonst als Versprechen ohne Funktion.)
 */
import { computed, onMounted } from 'vue';
import Icon from './Icon.vue';
import NotificationBell from './NotificationBell.vue';
import UserMenu from './UserMenu.vue';
import { useAuthStore } from '../stores/auth';
import { useTaskStore } from '../stores/tasks';

const auth = useAuthStore();
const tasks = useTaskStore();

interface NavEintrag {
  name: string;
  label: string;
  icon: string;
  sichtbar: boolean;
  /** Zahl im Abzeichen; `0` blendet es aus. */
  badge?: number;
}

// Reihenfolge nach Spec §12. „Benutzer" und „Einstellungen" sieht nur, wer sie auch
// aufrufen darf — ein Eintrag, der beim Klick aufs Dashboard zurückwirft, ist
// schlechter als gar keiner.
const navigation = computed<NavEintrag[]>(() => [
  { name: 'dashboard', label: 'Dashboard', icon: 'dashboard', sichtbar: true },
  { name: 'produkte', label: 'Produkte', icon: 'produkte', sichtbar: true },
  { name: 'priorisierung', label: 'Priorisierung', icon: 'priorisierung', sichtbar: true },
  { name: 'aufgaben', label: 'Aufgaben', icon: 'aufgaben', sichtbar: true, badge: tasks.offen },
  { name: 'benutzer', label: 'Benutzer', icon: 'benutzer', sichtbar: auth.canAdminister },
  {
    name: 'einstellungen',
    label: 'Einstellungen',
    icon: 'einstellungen',
    sichtbar: auth.canAdminister
  }
]);

const sichtbareNavigation = computed(() => navigation.value.filter((eintrag) => eintrag.sichtbar));

// Einmal die offene Zählung holen, damit das Abzeichen von Anfang an stimmt.
onMounted(() => void tasks.frischeZaehlung());
</script>

<template>
  <div class="shell">
    <header class="shell__header">
      <RouterLink :to="{ name: 'dashboard' }" class="shell__brand">
        <span class="shell__brand-mark">PM</span>
        <span class="shell__brand-name">PMPro</span>
      </RouterLink>

      <div class="shell__user">
        <NotificationBell />
        <UserMenu />
      </div>
    </header>

    <div class="shell__body">
      <nav class="shell__nav" aria-label="Hauptnavigation">
        <RouterLink
          v-for="eintrag in sichtbareNavigation"
          :key="eintrag.name"
          :to="{ name: eintrag.name }"
          class="shell__nav-item"
        >
          <Icon :name="eintrag.icon" :size="17" />
          <span>{{ eintrag.label }}</span>
          <span v-if="eintrag.badge" class="shell__badge">{{ eintrag.badge }}</span>
        </RouterLink>
      </nav>

      <main class="shell__main">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.shell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  height: var(--appbar-height);
  padding: 0 1.25rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-chrome-tint);
}

.shell__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: var(--color-text);
  font-weight: 600;
}
.shell__brand:hover {
  text-decoration: none;
}

.shell__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.shell__brand-name {
  font-size: 0.9375rem;
}

.shell__user {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.shell__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.shell__nav {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 216px;
  flex-shrink: 0;
  padding: 0.75rem;
  border-right: 1px solid var(--color-border);
  background: var(--color-surface-2);
  overflow-y: auto;
}

.shell__nav-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 0.65rem;
  border-radius: var(--radius);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  transition:
    background var(--t-fast),
    color var(--t-fast);
}
.shell__nav-item:hover {
  background: var(--color-bg);
  color: var(--color-text);
  text-decoration: none;
}

/* Abzeichen für offene Aufgaben — rechtsbündig, dezent aber sichtbar. */
.shell__badge {
  margin-left: auto;
  min-width: 1.25rem;
  padding: 0 0.35rem;
  border-radius: var(--radius-pill);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.25rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

/* `router-link-active` reicht hier nicht: „/" wäre sonst auf jeder Unterseite
   mitmarkiert. Die exakte Variante markiert genau den aktiven Bereich. */
.shell__nav-item.router-link-exact-active,
.shell__nav-item.router-link-active:not([href='/']) {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.shell__main {
  flex: 1;
  min-width: 0;
  padding: 1.5rem;
  overflow-y: auto;
}
</style>
